import React, { useEffect, useState } from 'react';
import { useFirebase } from './Firebase';
import { difficultyTypeMitsuruToStringSv7, gradeToString, clearTypeToString, getJacketImageSv7, getBorderColor, difficultyTypeToColor, difficultyTypeToAcronym, sv7DifnumToDisplay, sv7DifnumToLevel, sv7ClearMultipliers, CLEAR} from './musicUtils';
import { Box, Container, Card, Typography, CardMedia, Divider, useMediaQuery } from '@mui/material';
import { apiFetch } from './api';

const VolforceSv7 = () => {
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [scores, setScores] = useState([]);
  const { user } = useFirebase();
  const [metadata, setMetadata] = useState([]);
  const [overallVolforce, setOverallVolforce] = useState(0);
  const [top50, setTop50] = useState([]);
  const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down('sm'));

  useEffect(() => {
    if (!user) return;
    if (isDataLoaded) return;

    apiFetch('/api/volforceScores', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
    })
    .then(response => response.json())
    .then(scoreData => {
      setScores(scoreData);

      const uniqueMusicData = extractUniqueMusicData(scoreData);

      apiFetch('/api/sv7/volforceMetadata', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ musicData: uniqueMusicData }),
      })
      .then(response => response.json())
      .then(metadataData => {
        setMetadata(metadataData);

        const calculatedScores = calculateVolforce(scoreData, metadataData);
        const sortedScores = calculatedScores.sort((a, b) => b.volforce - a.volforce);

        const overallVF = sortedScores.slice(0, 50).reduce((sum, score) => sum + score.volforce, 0);
        setOverallVolforce(overallVF);

        setTop50(sortedScores.slice(0, 50));
      });
    });
    setIsDataLoaded(true);
  }, [user, scores, metadata]);

  const extractUniqueMusicData = (scoreData) => {
    const uniqueMusicData = [];
    const seen = new Set();

    scoreData.forEach(({ music_id, music_type }) => {
      const key = `${music_id}-${music_type}`;
      if (!seen.has(key)) {
        seen.add(key);
        uniqueMusicData.push({
          music_id,
          music_type: difficultyTypeMitsuruToStringSv7[music_type],
        });
      }
    });

    return uniqueMusicData;
  };

  // SV7 grade coefficient based on score thresholds
  const getGradeCoefficient = (score) => {
    if (score >= 9900000) return 1.05;  // S
    if (score >= 9800000) return 1.02;  // AAA+
    if (score >= 9700000) return 1.00;  // AAA
    if (score >= 9500000) return 0.97;  // AA+
    if (score >= 9300000) return 0.94;  // AA
    if (score >= 9000000) return 0.91;  // A+
    if (score >= 8700000) return 0.88;  // A
    if (score >= 7500000) return 0.85;  // B
    if (score >= 6500000) return 0.82;  // C
    return 0.80;                         // D
  };

  const calculateVolforce = (scoreData, metadataData) => {
    return scoreData.map(score => {
      const meta = metadataData.data.find(m => m.music_id === score.music_id && m.difficulty_type === difficultyTypeMitsuruToStringSv7[score.music_type]);
      if (!meta) return null;

      const level = sv7DifnumToLevel(meta.difnum);
      const gradeCoeff = getGradeCoefficient(score.score);
      const medalCoeff = sv7ClearMultipliers[score.clear] || 0.50;

      const raw = level * (score.score / 10000000) * gradeCoeff * medalCoeff * 20.0;
      // Kept in the game's own units: a floored integer of milli-volforce, so
      // 412 means 0.412 VF. Summing integers avoids the floating point drift
      // that the SV6 page has to round away. Divide only when displaying.
      const volforce = Math.floor(raw);

      return { ...score, ...meta, volforce };
    }).filter(Boolean);
  };

  const CustomCardMedia = ({ defaultImage, fallbackImage, placeholderImage, alt }) => {
    const [currentImage, setCurrentImage] = useState(defaultImage);

    useEffect(() => {
      const img = new Image();
      img.src = currentImage;

      img.onload = () => {
        setCurrentImage(currentImage);
      };

      img.onerror = () => {
        if (currentImage === defaultImage) {
          setCurrentImage(fallbackImage);
        } else if (currentImage === fallbackImage) {
          setCurrentImage(placeholderImage);
        }
      };
    }, [currentImage, defaultImage, fallbackImage, placeholderImage]);

    return (
      <CardMedia
        component="img"
        sx={{ width: "90px", height: "90px", flexShrink: 0, borderRadius: '6px', boxShadow: '0px 0px 7px #ffffff45'}}
        image={currentImage}
        alt={alt}
      />
    );
  };

  return (
    <Container>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', mb: 2}}>
        <Box>
          <Typography variant={isSmallScreen ? 'h4' : 'h2'} align="left" sx={{fontWeight: 'bold'}}>
            SV7 Volforce
          </Typography>
        </Box>
        <Box>
          <Typography variant="h5" align="right" component="div">
            Volforce Rating:
          </Typography>
          <Typography variant="h3" align="right" component="div" sx={{fontWeight: 'bold'}}>
            {(overallVolforce / 1000).toFixed(3)}
          </Typography>
        </Box>
      </Box>
      <Divider sx={{mt: 1, mb: 2}} />
      <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center' }}>
        {top50.map((score, index) => {

          const { defaultImage, fallbackImage } = getJacketImageSv7({ imageID: score.music_id, difficultyType: difficultyTypeMitsuruToStringSv7[score.music_type] });
          const borderColor = getBorderColor(score.clear, score.grade);
          const diffAcronym = difficultyTypeToAcronym(difficultyTypeMitsuruToStringSv7[score.music_type], score.inf_ver);

          return (
            <Box key={index} sx={{ minWidth: 200, flexBasis: 'calc(20% - 12px)', margin: 0.75 }}>
              <Card
                sx={{
                  minWidth: 180,
                  flexDirection: 'column',
                  background: score.clear === CLEAR.PUC
                    ? "linear-gradient(to top left, #FF9AA299 0%, #FFB7B299 5%, #FFDAC199 10%, #E2F0CB99 15%, #B5EAD799 20%, #C7CEEA99 25%, #9AA2FF99 30%, #20202099 35%, transparent), linear-gradient(to bottom right, #202020, transparent)"
                    : `linear-gradient(to top left, ${borderColor}, #202020 35%)`
                }}
              >
                <Box display="flex" flexDirection="column" p={1} sx={{ position: 'relative' }}>
                  <Box display="flex" justifyContent="space-between">
                    <Box sx={{ position: 'relative' }}>
                      <CustomCardMedia
                        defaultImage={defaultImage}
                        fallbackImage={fallbackImage}
                        placeholderImage="/content/jk_placeholder.png"
                        alt={score.title_name || "Unknown Song"}
                      />
                      <Box
                        sx={{
                          position: 'absolute',
                          top: '5px',
                          right: '-33px',
                          backgroundColor: difficultyTypeToColor[diffAcronym],
                          borderColor: 'white',
                          color: 'white',
                          borderRadius: '4px',
                          padding: '0 5px 2px 4px',
                          fontSize: '0.85em',
                          fontWeight: 'bold',
                          textShadow: '1.5px 1.5px 2px black',
                          boxShadow: '0px 0px 8px #ffffffEE'
                        }}
                      >
                        {diffAcronym} {sv7DifnumToDisplay(score.difnum)}
                      </Box>
                    </Box>
                    <Box>
                      <Box sx={{ marginTop: -0.5 }}>
                        <Typography variant="h5" component="div" sx={{ fontWeight: 'bold', textAlign: 'right', textShadow: '0px 0px 7px #ffffff77' }}>
                          {(score.volforce / 10).toFixed(1)}
                        </Typography>
                        <Typography variant="h7" component="div" sx={{ textAlign: 'right', color: '#d1d1d1', marginTop: '6px' }}>
                          {score.score}
                        </Typography>
                      </Box>
                      <Box display="flex" justifyContent="flex-end" sx={{ marginTop: '6px' }}>
                        <Box sx={{ backgroundColor: '#4f51aed4', padding: '3px', borderRadius: '2px', boxShadow: '0px 0px 7px #ffffff45'}}>
                          <Typography variant="body2" component="div" sx={{ fontWeight: 'bold' }}>
                            {gradeToString[score.grade]} | {clearTypeToString[score.clear]}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </Box>
                <Box p={1} mt={-1}>
                  <Typography
                    variant="body2"
                    component="div"
                    sx={{
                      fontWeight: 'bold',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      maxWidth: '100%',
                      textShadow: '1.5px 1.5px 1.5px #00000066',
                    }}
                  >
                    {score.title_name || "Unknown Song"}
                  </Typography>
                </Box>
              </Card>
            </Box>
          );
        })}
      </Box>
    </Container>
  );

};

export default VolforceSv7;
